import { useId } from "react";
import { ArrowLeftIcon } from "lucide-react";

import { cn } from "@/components/ui/utils";

export function StickyLayout({
  className,
  stickyTopChildren,
  stickyTopClassName,
  stickyBottomChildren,
  stickyBottomClassName,
  children,
  childrenClassName,
  fillHeight = true,
}: {
  /** The whole layout's own look, over its column. */
  className?: string;
  stickyTopChildren?: React.ReactNode;
  stickyTopClassName?: string;
  stickyBottomChildren?: React.ReactNode;
  stickyBottomClassName?: string;
  children: React.ReactNode;
  /** The scrolling content's own look, over its column. */
  childrenClassName?: string;
  /** When false, size to content instead of filling the parent (avoids Safari flex gaps in content-sized dialogs). */
  fillHeight?: boolean;
}) {
  const id = useId();
  return (
    <div
      id={`$sticky-scrollable-layout-${id}`}
      // Shrinks to what holds it - a dialog as tall as the screen allows -
      // so what scrolls is its content, under its top and bottom, not the
      // whole of it, its top included, inside what holds it.
      className={cn(
        "flex flex-col flex-nowrap",
        fillHeight ? "h-full" : "h-auto",
        "min-h-0 w-full",
        className,
      )}
    >
      {stickyTopChildren && (
        <div
          id={`sticky-scrollable-layout-top-${id}`}
          className={cn(
            "min-w-0",
            "flex flex-row justify-start items-center sticky top-0 z-1000 w-full [border-bottom-width:1px] [border-bottom-style:solid] border-border gap-2 pl-2 pr-2 h-12 min-h-min",
            stickyTopClassName,
          )}
        >
          {stickyTopChildren}
        </div>
      )}

      <div
        id={`sticky-scrollable-layout-content-${id}`}
        className={cn(
          // A grid container's, as Joy's was: a flex box that may shrink.
          "min-w-0 box-border flex m-0",
          "flex-col w-full max-w-full flex-nowrap overflow-auto",
          // What is left under its top and over its bottom, as a share of
          // the column: Safari reads no ``stretch``.
          fillHeight ? "flex-1 min-h-0" : "h-auto",
          childrenClassName,
        )}
      >
        {children}
      </div>

      {stickyBottomChildren && (
        <div
          id={`sticky-scrollable-layout-bottom-${id}`}
          className={cn(
            "flex min-w-0 flex-wrap",
            "flex-row flex-nowrap justify-end gap-4 items-center sticky bottom-0 z-1000 [border-top-width:1px] [border-top-style:solid] border-border p-2 h-12 min-h-fit w-full max-w-full bg-transparent",
            stickyBottomClassName,
          )}
        >
          {stickyBottomChildren}
        </div>
      )}
    </div>
  );
}
